/**
 * Calendar Event Editor Types
 */

import {
  CalendarEventDto,
  CalendarEventStatus,
  EventOwnerEntityType,
  RsvpStatus,
} from '@api/types/calendarEvent'

// Re-export enums for convenience
export { CalendarEventStatus, EventOwnerEntityType, RsvpStatus }

// ====================
// EDITOR STATE
// ====================

/**
 * Section IDs for editor navigation
 */
export type CalendarEventSectionId =
  | 'details'
  | 'datetime'
  | 'location'
  | 'tags'
  | 'files'
  | 'settings'
  | 'criteria'

/**
 * Valid section IDs for URL hash navigation
 */
export const VALID_SECTION_IDS: CalendarEventSectionId[] = [
  'details',
  'datetime',
  'location',
  'tags',
  'files',
  'settings',
  'criteria',
]

/**
 * Form data for event editing
 */
export interface CalendarEventFormData {
  title: string
  quickDescription: string
  fullDescription: string
  startDateTime: Date | null
  endDateTime: Date | null
  timezone: string | null
  isAllDay: boolean
  locationName: string
  latitude: number | null
  longitude: number | null
  isPublic: boolean
  maxAttendees: number | null
  linkedAdventureId: string | null
  coverImageId: string | null
  coverImageUrl: string | null
}

/**
 * Owner selection for group/business events
 */
export interface EventOwnerSelection {
  entityId: string
  entityType: EventOwnerEntityType
  entityName: string
  entityAvatarUrl?: string
}

/**
 * Editor validation state
 */
export interface CalendarEventValidationState {
  isValid: boolean
  errors: {
    title?: string
    quickDescription?: string
    startDateTime?: string
    endDateTime?: string
    location?: string
  }
}

/**
 * Create initial form data
 */
export function createInitialFormData(): CalendarEventFormData {
  // Default start time: next hour
  const now = new Date()
  const startDateTime = new Date(now)
  startDateTime.setHours(startDateTime.getHours() + 1, 0, 0, 0)

  // Default end time: 2 hours after start
  const endDateTime = new Date(startDateTime)
  endDateTime.setHours(endDateTime.getHours() + 2)

  return {
    title: '',
    quickDescription: '',
    fullDescription: '',
    startDateTime,
    endDateTime,
    timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    isAllDay: false,
    locationName: '',
    latitude: null,
    longitude: null,
    isPublic: true,
    maxAttendees: null,
    linkedAdventureId: null,
    coverImageId: null,
    coverImageUrl: null,
  }
}

/**
 * Convert CalendarEventDto to form data
 */
export function eventToFormData(event: CalendarEventDto): CalendarEventFormData {
  return {
    title: event.title,
    quickDescription: event.quickDescription,
    fullDescription: event.fullDescription || '',
    startDateTime: new Date(event.startDateTime),
    endDateTime: new Date(event.endDateTime),
    timezone: event.timezone || null,
    isAllDay: event.isAllDay,
    locationName: event.location?.name || '',
    latitude: event.location?.latitude ?? null,
    longitude: event.location?.longitude ?? null,
    isPublic: event.isPublic,
    maxAttendees: event.maxAttendees ?? null,
    linkedAdventureId: event.linkedAdventure?.adventureId ?? null,
    coverImageId: event.coverImageId ?? null,
    coverImageUrl: event.coverImageUrl ?? null,
  }
}

/**
 * Validate form data
 */
export function validateFormData(data: CalendarEventFormData): CalendarEventValidationState {
  const errors: CalendarEventValidationState['errors'] = {}

  // Title is required
  if (!data.title.trim()) {
    errors.title = 'Title is required'
  } else if (data.title.length > 100) {
    errors.title = 'Title must be 100 characters or less'
  }

  // Quick description is required
  if (!data.quickDescription.trim()) {
    errors.quickDescription = 'Quick description is required'
  } else if (data.quickDescription.length > 150) {
    errors.quickDescription = 'Quick description must be 150 characters or less'
  }

  // Start date is required
  if (!data.startDateTime) {
    errors.startDateTime = 'Start date/time is required'
  }

  // End date is required and must be after start
  if (!data.endDateTime) {
    errors.endDateTime = 'End date/time is required'
  } else if (data.startDateTime && data.endDateTime <= data.startDateTime) {
    errors.endDateTime = 'End date/time must be after start date/time'
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  }
}

/**
 * Check if form has unsaved changes
 */
export function hasFormChanges(
  original: CalendarEventFormData | null,
  current: CalendarEventFormData
): boolean {
  if (!original) return true

  return (
    original.title !== current.title ||
    original.quickDescription !== current.quickDescription ||
    original.fullDescription !== current.fullDescription ||
    original.startDateTime?.getTime() !== current.startDateTime?.getTime() ||
    original.endDateTime?.getTime() !== current.endDateTime?.getTime() ||
    original.timezone !== current.timezone ||
    original.isAllDay !== current.isAllDay ||
    original.locationName !== current.locationName ||
    original.latitude !== current.latitude ||
    original.longitude !== current.longitude ||
    original.isPublic !== current.isPublic ||
    original.maxAttendees !== current.maxAttendees ||
    original.linkedAdventureId !== current.linkedAdventureId ||
    original.coverImageId !== current.coverImageId
  )
}

// ====================
// HELPER TYPES
// ====================

/**
 * Status helper functions
 */
export function isEventDraft(status: CalendarEventStatus | number | undefined): boolean {
  const numStatus = typeof status === 'number' ? status : Number(status)
  return numStatus === CalendarEventStatus.Draft
}

export function isEventPublished(status: CalendarEventStatus | number | undefined): boolean {
  const numStatus = typeof status === 'number' ? status : Number(status)
  return numStatus === CalendarEventStatus.Published
}

export function isEventCancelled(status: CalendarEventStatus | number | undefined): boolean {
  const numStatus = typeof status === 'number' ? status : Number(status)
  return numStatus === CalendarEventStatus.Cancelled
}

export function isEventCompleted(status: CalendarEventStatus | number | undefined): boolean {
  const numStatus = typeof status === 'number' ? status : Number(status)
  return numStatus === CalendarEventStatus.Completed
}

export function isEventPast(endDateTime: string | Date): boolean {
  const endDate = typeof endDateTime === 'string' ? new Date(endDateTime) : endDateTime
  return endDate < new Date()
}

export function canEditEvent(
  status: CalendarEventStatus | number | undefined,
  canEdit?: boolean
): boolean {
  if (canEdit === false) return false
  return isEventDraft(status) || isEventPublished(status)
}

export function canRsvpToEvent(
  status: CalendarEventStatus | number | undefined,
  endDateTime: string | Date,
  isEventFull: boolean
): boolean {
  // Can't RSVP to draft, cancelled, or completed events
  if (!isEventPublished(status)) return false

  // Can't RSVP to past events
  if (isEventPast(endDateTime)) return false

  // Can't RSVP if event is full
  if (isEventFull) return false

  return true
}
